'use client';

import {
  Permission,
  WorkspaceMemberRole,
  WorkspaceMemberStatus,
  type WorkspaceMemberWithUserDto,
  type WorkspaceRoleListItemDto,
} from '@up2stay/up2promo-api-client';
import { Avatar, AvatarFallback, AvatarImage } from '@up2stay/up2promo-ui';
import { Badge } from '@up2stay/up2promo-ui';
import { Button } from '@up2stay/up2promo-ui';
import { Card, CardContent } from '@up2stay/up2promo-ui';
import {
  brandAvatarGlowClass,
  brandGradFillClass,
  cn,
} from '@up2stay/up2promo-ui';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@up2stay/up2promo-ui';
import { Skeleton } from '@up2stay/up2promo-ui';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@up2stay/up2promo-ui';
import { Mail, MoreHorizontal, Pencil, Trash2, Users } from 'lucide-react';

import { canEditWorkspaceMemberRole } from '@/core/authz/can-edit-workspace-member-role';
import {
  memberToRoleAssignment,
  resolveRoleDisplayName,
} from '@/core/authz/role-assignment';
import {
  ListEmptyState,
  resourceInteractiveCardClass,
} from '@/core/components/layout';
import {
  isPendingInvitation,
  memberDisplayName,
  memberInitials,
  memberInviteEmail,
} from '@/core/components/workspace/users/member-identity';
import { usePermissions } from '@/core/hooks/use-permissions';
import { getAbsoluteFileUrl } from '@/core/lib/file-url';
import { useDictionary, useLocale } from '@/core/providers/dictionary-provider';

interface UsersTableProps {
  members: WorkspaceMemberWithUserDto[];
  roles: WorkspaceRoleListItemDto[];
  teamsByUserId?: Record<string, string[]>;
  isLoading?: boolean;
  currentUserId?: string;
  currentUserRole?: WorkspaceMemberRole;
  onMemberClick?: (member: WorkspaceMemberWithUserDto) => void;
  onEditRole: (member: WorkspaceMemberWithUserDto) => void;
  onRemove: (member: WorkspaceMemberWithUserDto) => void;
  onResendInvite?: (member: WorkspaceMemberWithUserDto) => void;
  searchQuery?: string;
}

function getRoleBadgeVariant(
  role: WorkspaceMemberRole,
): 'default' | 'secondary' | 'outline' {
  switch (role) {
    case WorkspaceMemberRole.OWNER:
      return 'default';
    case WorkspaceMemberRole.ADMIN:
      return 'outline'; // Changed from 'secondary' to 'outline' for better contrast
    case WorkspaceMemberRole.MEMBER:
      return 'outline';
    default:
      return 'outline';
  }
}

function getStatusBadgeVariant(
  status: WorkspaceMemberStatus,
): 'default' | 'secondary' | 'outline' | 'destructive' {
  switch (status) {
    case WorkspaceMemberStatus.ACTIVE:
      return 'default';
    case WorkspaceMemberStatus.PENDING:
      return 'outline'; // Changed from 'secondary' to 'outline' for better readability
    case WorkspaceMemberStatus.REJECTED:
      return 'destructive';
    default:
      return 'outline';
  }
}

export function UsersTable({
  members,
  roles,
  teamsByUserId,
  isLoading,
  currentUserId,
  currentUserRole,
  onMemberClick,
  onEditRole,
  onRemove,
  onResendInvite,
  searchQuery,
}: UsersTableProps) {
  const dict = useDictionary();
  const locale = useLocale();
  const { can } = usePermissions();
  const builtinRoleLabels = dict.workspace.users.roles;
  const canManageMembers = can(Permission.MEMBERS_UPDATE_ROLE);
  const canRemoveMembers = can(Permission.MEMBERS_REMOVE);
  const canInviteMembers = can(Permission.MEMBERS_INVITE);
  const showActionsColumn =
    canManageMembers || canRemoveMembers || canInviteMembers;
  const dateLocale = locale === 'de' ? 'de-DE' : 'en-US';

  const formatMemberDate = (iso: string | undefined) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString(dateLocale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const canEditRole = (member: WorkspaceMemberWithUserDto) =>
    canEditWorkspaceMemberRole({
      canManageRole: canManageMembers,
      memberUserId: member.userId,
      memberRole: member.role,
      currentUserId,
      currentUserRole,
    });

  const canRemove = (member: WorkspaceMemberWithUserDto) => {
    if (!canRemoveMembers) return false;
    // Can't remove OWNER
    if (member.role === WorkspaceMemberRole.OWNER) return false;
    // Can't remove yourself
    if (member.userId === currentUserId) return false;
    return true;
  };

  const canResendInvite = (member: WorkspaceMemberWithUserDto) => {
    return (
      canInviteMembers &&
      isPendingInvitation(member) &&
      Boolean(onResendInvite) &&
      Boolean(memberInviteEmail(member))
    );
  };

  if (isLoading) {
    return (
      <Card className="min-w-0 overflow-hidden">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{dict.workspace.users.table.user}</TableHead>
                <TableHead>{dict.workspace.users.table.team}</TableHead>
                <TableHead>{dict.workspace.users.table.role}</TableHead>
                <TableHead>{dict.workspace.users.table.status}</TableHead>
                <TableHead>
                  {dict.workspace.users.table.joinedOrInvited}
                </TableHead>
                {showActionsColumn && (
                  <TableHead className="w-[70px]">
                    {dict.workspace.users.table.actions}
                  </TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {[1, 2, 3].map((i) => (
                <TableRow key={i}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Skeleton className="size-10 rounded-full" />
                      <div className="flex flex-col gap-1">
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-3 w-48" />
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-24" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-5 w-16" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-5 w-16" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-24" />
                  </TableCell>
                  {showActionsColumn && (
                    <TableCell>
                      <Skeleton className="h-8 w-8" />
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    );
  }

  if (members.length === 0) {
    return (
      <ListEmptyState
        icon={<Users className="h-6 w-6 text-muted-foreground" />}
        title={
          searchQuery?.trim()
            ? dict.workspace.users.table.noResults
            : dict.workspace.users.table.empty
        }
      />
    );
  }

  return (
    <>
      {/* Desktop Table View — composition via viewport `md`, not Cap shell */}
      <Card className="hidden min-w-0 overflow-hidden md:block">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{dict.workspace.users.table.user}</TableHead>
                <TableHead>{dict.workspace.users.table.team}</TableHead>
                <TableHead>{dict.workspace.users.table.role}</TableHead>
                <TableHead>{dict.workspace.users.table.status}</TableHead>
                <TableHead>
                  {dict.workspace.users.table.joinedOrInvited}
                </TableHead>
                {showActionsColumn && (
                  <TableHead className="w-[70px]">
                    {dict.workspace.users.table.actions}
                  </TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {members.map((member) => {
                const user = member.user;
                const teamNames = (
                  teamsByUserId?.[member.userId ?? ''] ?? []
                ).filter(Boolean);
                const teamsLabel = teamNames.length
                  ? teamNames.join(', ')
                  : '—';
                const displayName = memberDisplayName(
                  member,
                  dict.workspace.users.common.unknownUser,
                );
                const initials = memberInitials(member);
                const email = memberInviteEmail(member);
                const joinedDate = formatMemberDate(member.createdAt);

                return (
                  <TableRow
                    key={member.id}
                    className={
                      onMemberClick
                        ? 'cursor-pointer hover:bg-[color:var(--ghost-accent-bg)]/40'
                        : undefined
                    }
                    onClick={
                      onMemberClick ? () => onMemberClick(member) : undefined
                    }
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className={cn('size-10', brandAvatarGlowClass)}>
                          <AvatarImage
                            src={getAbsoluteFileUrl(user?.photo?.path)}
                            alt={displayName}
                          />
                          <AvatarFallback
                            className={cn(brandGradFillClass, 'text-[11px]')}
                          >
                            {initials}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          {onMemberClick ? (
                            // Keyboard/screen-reader entry to the row action:
                            // its click bubbles to the row's onClick (DEV-540).
                            <button
                              type="button"
                              className="block max-w-full truncate rounded-sm text-left text-[13.5px] font-semibold leading-relaxed text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            >
                              {displayName}
                            </button>
                          ) : (
                            <div className="truncate text-[13.5px] font-semibold leading-relaxed text-foreground">
                              {displayName}
                            </div>
                          )}
                          <div className="truncate text-[13.5px] leading-relaxed text-muted-foreground">
                            {email || '—'}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="max-w-[260px] truncate text-[13.5px] leading-relaxed text-muted-foreground">
                      {teamsLabel}
                    </TableCell>
                    <TableCell>
                      <Badge variant={getRoleBadgeVariant(member.role)}>
                        {resolveRoleDisplayName(
                          memberToRoleAssignment(member),
                          roles,
                          builtinRoleLabels,
                        )}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={getStatusBadgeVariant(member.status)}>
                        {dict.workspace.users.status[
                          member.status as keyof typeof dict.workspace.users.status
                        ] ?? member.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-[13.5px] leading-relaxed text-muted-foreground">
                      {joinedDate}
                    </TableCell>
                    {showActionsColumn && (
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                            >
                              <MoreHorizontal className="h-4 w-4" />
                              <span className="sr-only">
                                {dict.workspace.users.table.actions}
                              </span>
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {canResendInvite(member) && (
                              <DropdownMenuItem
                                onClick={() => onResendInvite?.(member)}
                              >
                                <Mail className="mr-2 h-4 w-4" />
                                {dict.workspace.users.actions.resendInvite}
                              </DropdownMenuItem>
                            )}
                            {canEditRole(member) && (
                              <DropdownMenuItem
                                onClick={() => onEditRole(member)}
                              >
                                <Pencil className="mr-2 h-4 w-4" />
                                {dict.workspace.users.actions.changeRole}
                              </DropdownMenuItem>
                            )}
                            {canRemove(member) && (
                              <>
                                {(canEditRole(member) ||
                                  canResendInvite(member)) && (
                                  <DropdownMenuSeparator />
                                )}
                                <DropdownMenuItem
                                  onClick={() => onRemove(member)}
                                  className="text-destructive"
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  {dict.workspace.users.actions.remove}
                                </DropdownMenuItem>
                              </>
                            )}
                            {!canEditRole(member) &&
                              !canRemove(member) &&
                              !canResendInvite(member) && (
                                <DropdownMenuItem disabled>
                                  {
                                    dict.workspace.users.actions
                                      .noActionsAvailable
                                  }
                                </DropdownMenuItem>
                              )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Mobile card list — composition via viewport `md`, not Cap shell */}
      <div className="flex flex-col gap-3 md:hidden">
        {members.map((member) => {
          const user = member.user;
          const teamNames = (teamsByUserId?.[member.userId ?? ''] ?? []).filter(
            Boolean,
          );
          const teamsLabel = teamNames.length ? teamNames.join(', ') : '—';
          const displayName = memberDisplayName(
            member,
            dict.workspace.users.common.unknownUser,
          );
          const initials = memberInitials(member);
          const email = memberInviteEmail(member);
          const joinedDate = formatMemberDate(member.createdAt);

          return (
            <Card
              key={member.id}
              role={onMemberClick ? 'button' : undefined}
              tabIndex={onMemberClick ? 0 : undefined}
              onClick={onMemberClick ? () => onMemberClick(member) : undefined}
              onKeyDown={
                onMemberClick
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onMemberClick(member);
                      }
                    }
                  : undefined
              }
              className={cn(
                'min-w-0 overflow-hidden',
                onMemberClick &&
                  cn('cursor-pointer', resourceInteractiveCardClass),
              )}
            >
              <CardContent className="flex flex-col gap-3 p-5 sm:p-6">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 flex-1 items-center gap-3">
                    <Avatar
                      className={cn('size-10 shrink-0', brandAvatarGlowClass)}
                    >
                      <AvatarImage
                        src={getAbsoluteFileUrl(user?.photo?.path)}
                        alt={displayName}
                      />
                      <AvatarFallback
                        className={cn(brandGradFillClass, 'text-[11px]')}
                      >
                        {initials}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[15.5px] font-bold leading-tight text-foreground">
                        {displayName}
                      </div>
                      <div className="truncate text-[13.5px] leading-relaxed text-muted-foreground">
                        {email || '—'}
                      </div>
                    </div>
                  </div>
                  {showActionsColumn && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 shrink-0"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <MoreHorizontal className="h-4 w-4" />
                          <span className="sr-only">
                            {dict.workspace.users.table.actions}
                          </span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {canResendInvite(member) && (
                          <DropdownMenuItem
                            onClick={() => onResendInvite?.(member)}
                          >
                            <Mail className="mr-2 h-4 w-4" />
                            {dict.workspace.users.actions.resendInvite}
                          </DropdownMenuItem>
                        )}
                        {canEditRole(member) && (
                          <DropdownMenuItem onClick={() => onEditRole(member)}>
                            <Pencil className="mr-2 h-4 w-4" />
                            {dict.workspace.users.actions.changeRole}
                          </DropdownMenuItem>
                        )}
                        {canRemove(member) && (
                          <>
                            {(canEditRole(member) ||
                              canResendInvite(member)) && (
                              <DropdownMenuSeparator />
                            )}
                            <DropdownMenuItem
                              onClick={() => onRemove(member)}
                              className="text-destructive"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              {dict.workspace.users.actions.remove}
                            </DropdownMenuItem>
                          </>
                        )}
                        {!canEditRole(member) &&
                          !canRemove(member) &&
                          !canResendInvite(member) && (
                            <DropdownMenuItem disabled>
                              {dict.workspace.users.actions.noActionsAvailable}
                            </DropdownMenuItem>
                          )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={getRoleBadgeVariant(member.role)}>
                    {resolveRoleDisplayName(
                      memberToRoleAssignment(member),
                      roles,
                      builtinRoleLabels,
                    )}
                  </Badge>
                  <Badge variant={getStatusBadgeVariant(member.status)}>
                    {dict.workspace.users.status[
                      member.status as keyof typeof dict.workspace.users.status
                    ] ?? member.status}
                  </Badge>
                  <span className="text-[11px] leading-relaxed text-muted-foreground">
                    {dict.workspace.users.table.team}: {teamsLabel}
                  </span>
                  <span className="ml-auto text-[11px] leading-relaxed text-muted-foreground">
                    {dict.workspace.users.table.joinedOrInvited}: {joinedDate}
                  </span>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </>
  );
}
